import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, idr, idrShort } from "../lib/api";
import {
  Package, ArrowLeft, User, Users2, History, MapPin, Calendar,
  TrendingDown, Tag, Barcode, Smartphone, Printer, AlertCircle,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggalPanjang, formatTanggal } from "@/lib/tanggal";

export default function AssetScan() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/assets/${id}/scan-detail`);
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Aset tidak ditemukan atau butuh login");
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 grid place-items-center p-6">
      <LoadingState loading jumlahKartu={1} data-testid="asset-scan-loading" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 grid place-items-center p-6">
      <div className="max-w-md bg-white rounded-2xl border border-rose-200 p-8 text-center shadow-lg">
        <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
        <h2 className="font-display font-bold text-lg text-slate-900 mb-2">Aset Tidak Ditemukan</h2>
        <p className="text-sm text-slate-500 mb-6">{error}</p>
        <Link to="/assets" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Buka Daftar Aset
        </Link>
      </div>
    </div>
  );

  const a = data.asset;
  const dep = a.metode_penyusutan === "Garis Lurus" && a.umur_manfaat_bulan > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-slate-50 pb-12">
      {/* Sticky Header */}
      <header className="sticky top-0 z-10 bg-emerald-700 text-white shadow-md">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/assets" className="p-1.5 hover:bg-emerald-600 rounded-lg" data-testid="scan-back">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-100">Aset Scan</p>
            <h1 className="font-display font-semibold text-sm truncate">{a.nama}</h1>
          </div>
          <Package className="w-5 h-5" />
        </div>
      </header>

      <div className="max-w-md mx-auto p-4 space-y-4" data-testid="asset-scan-page">
        {/* Hero card: nama + kode + status */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-mono text-emerald-700 uppercase tracking-wider">
                {a.kode || a.id.slice(0, 8).toUpperCase()}
              </p>
              <h2 className="font-display font-bold text-lg text-slate-900 mt-0.5">{a.nama}</h2>
              {a.merk && <p className="text-xs text-slate-500">{a.merk} · {a.kategori || "-"}</p>}
            </div>
            <span className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-bold ${
              a.kondisi === "Baik" ? "bg-emerald-100 text-emerald-800" :
              a.kondisi === "Rusak Ringan" ? "bg-amber-100 text-amber-800" :
              "bg-rose-100 text-rose-800"
            }`}>{a.kondisi}</span>
          </div>

          {/* IDs */}
          <div className="grid grid-cols-1 gap-2 pt-3 border-t border-slate-100 text-xs">
            {a.serial_no && (
              <div className="flex items-center gap-2">
                <Barcode className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-slate-500 shrink-0">Serial:</span>
                <span className="font-mono text-slate-900 break-all">{a.serial_no}</span>
              </div>
            )}
            {a.imei && (
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-slate-500 shrink-0">IMEI:</span>
                <span className="font-mono text-slate-900 break-all">{a.imei}</span>
              </div>
            )}
            {a.tgl_serah_terima && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-slate-500 shrink-0">Tanggal:</span>
                <span className="text-slate-900">{formatTanggalPanjang(a.tgl_serah_terima)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Nilai & Depresiasi */}
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Nilai Perolehan</p>
          <p className="font-display font-bold text-2xl text-slate-900">{idr(a.nilai || 0)}</p>
          {dep && (
            <>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-600 inline-flex items-center gap-1">
                    <TrendingDown className="w-3.5 h-3.5 text-amber-600" /> Nilai Buku
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    (a.sisa_pct || 0) >= 60 ? "bg-emerald-100 text-emerald-800" :
                    (a.sisa_pct || 0) >= 30 ? "bg-amber-100 text-amber-800" :
                    "bg-rose-100 text-rose-800"
                  }`}>{a.sisa_pct?.toFixed?.(1) || 0}%</span>
                </div>
                {/* Backend `_compute_depreciation` (routers/assets.py) mengirim nilai
                    buku sebagai `nilai_saat_ini`; field `nilai_buku` tidak pernah ada
                    sehingga nilai buku selalu tampil Rp 0. */}
                <p className="font-mono font-bold text-lg text-slate-900">{idr(a.nilai_saat_ini || 0)}</p>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-2">
                  <div className={`h-full ${
                    (a.sisa_pct || 0) >= 60 ? "bg-emerald-500" :
                    (a.sisa_pct || 0) >= 30 ? "bg-amber-500" :
                    "bg-rose-500"
                  }`} style={{ width: `${Math.min(100, a.sisa_pct || 0)}%` }} />
                </div>
                <p className="text-[10px] text-slate-400 mt-2">
                  Metode: {a.metode_penyusutan} · Umur {a.umur_manfaat_bulan} bulan · Residu {idrShort(a.nilai_residu || 0)}
                </p>
              </div>
            </>
          )}
          {!dep && (
            <p className="text-[10px] text-slate-400 mt-2">
              {a.metode_penyusutan === "Non-penyusutan" ? "Non-penyusutan (nilai tetap)" : "Tanpa depresiasi"}
            </p>
          )}
        </div>

        {/* Pemegang */}
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-5" data-testid="scan-holders">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Pemegang Aset</p>
          {data.primary_holder ? (
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-emerald-600 text-white grid place-items-center font-bold text-sm">
                {(data.primary_holder.nama || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-900 truncate">{data.primary_holder.nama}</p>
                <p className="text-[11px] text-slate-500">{data.primary_holder.branch || "-"}</p>
              </div>
              <span className="shrink-0 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold">Utama</span>
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">Belum ditugaskan ke karyawan</p>
          )}
          {(data.additional_holders || []).length > 0 && (
            <>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mt-3 mb-2">
                <Users2 className="w-3 h-3 inline mb-0.5 mr-1" /> Pemegang Tambahan ({data.additional_holders.length})
              </p>
              <div className="space-y-1.5">
                {data.additional_holders.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 border border-slate-200 rounded-lg p-2">
                    <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 grid place-items-center text-xs font-bold">
                      {(h.employee?.nama || h.employee_nama || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-900 truncate">{h.employee?.nama || h.employee_nama || "-"}</p>
                      <p className="text-[10px] text-slate-500">{h.peran || "pemegang"}{h.catatan ? ` · ${h.catatan}` : ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Riwayat */}
        {(data.history || []).length > 0 && (
          <div className="bg-white rounded-2xl shadow border border-slate-200 p-5" data-testid="scan-history">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-3">
              <History className="w-3 h-3 inline mb-0.5 mr-1" /> Riwayat ({data.history.length})
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data.history.map((h, i) => (
                <div key={h.id || i} className="border-l-2 border-emerald-300 pl-3 py-1.5">
                  <p className="text-xs font-medium text-slate-900">{h.action}</p>
                  {h.note && <p className="text-[11px] text-slate-500 mt-0.5">{h.note}</p>}
                  {/* `_log_asset_change` menyimpan pelakunya di `actor_email`, bukan
                      `by_user` — makanya kolom pelaku dulu selalu "-". */}
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {h.actor_email || "-"} · {formatTanggal(h.at, { denganJam: true })}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Catatan */}
        {a.catatan && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1">Catatan</p>
            <p className="text-xs text-amber-900 italic">{a.catatan}</p>
          </div>
        )}

        <div className="text-center pt-4">
          <Link to="/assets" data-testid="scan-open-full"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
            <Package className="w-4 h-4" /> Buka Daftar Aset Lengkap
          </Link>
        </div>
      </div>
    </div>
  );
}
